(() => {
  const fields = ['audience', 'finish', 'style', 'category'];
  const isAny = value => !value || value === 'any';
  function rankGiftProducts(products, answers, limit = 4) {
    const maximum = isAny(answers.budget) ? Infinity : Number(answers.budget);
    const selected = [];
    const seen = new Set();
    const ranked = products.map((product, index) => ({ ...product, rank: Number.isFinite(Number(product.rank)) ? Number(product.rank) : index }))
      .filter(product => product.available !== false && !product.excluded &&
        !/custom|mystery/i.test(String(product.handle || '') + ' ' + String(product.title || '')) &&
        Number.isFinite(product.price) && product.price > 0 && product.price <= maximum &&
        fields.every(field => isAny(answers[field]) || product[field] === answers[field]))
      .sort((a, b) => a.rank - b.rank || a.price - b.price);
    for (const product of ranked) {
      if (seen.has(String(product.id))) continue;
      seen.add(String(product.id));
      selected.push({ ...product, exact: true, differences: [] });
      if (selected.length === limit) break;
    }
    return { products: selected, exactCount: selected.length, maximum };
  }
  if (typeof module === 'object' && module.exports) module.exports = { rankGiftProducts };
  if (typeof customElements === 'undefined' || customElements.get('iceyard-gift-guide')) return;
  customElements.define('iceyard-gift-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('[data-gift-dialog]');
      this.trigger = this.querySelector('[data-gift-open]');
      this.steps = [...this.querySelectorAll('[data-gift-step]')];
      this.form = this.querySelector('[data-gift-form]');
      this.results = this.querySelector('[data-gift-results]');
      this.body = this.querySelector('[data-gift-body]');
      this.back = this.querySelector('[data-gift-back]');
      this.next = this.querySelector('[data-gift-next]');
      this.progress = this.querySelector('[data-gift-progress]');
      this.progressText = this.querySelector('[data-gift-progress-text]');
      this.status = this.querySelector('[data-gift-status]');
      this.copy = Object.fromEntries([...this.querySelectorAll('[data-gift-copy]')].map(node => [node.dataset.giftCopy, node.textContent]));
      this.products = [];
      this.catalogCache = new Map();
      this.resultToken = 0;
      this.progress.max = this.steps.length;
      this.answers = {};
      this.step = 0;
      this.showingResults = false;
      this.trigger.hidden = false;
      this.querySelector('[data-gift-fallback]').hidden = true;
      this.trigger.addEventListener('click', event => this.open(event.currentTarget), options);
      this.querySelector('[data-gift-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => { this.resultToken++; this.lastTrigger?.focus({ preventScroll: true }); }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.form.addEventListener('change', event => {
        if (!event.target.matches('input[type=radio]')) return;
        this.answers[event.target.name.replace(this.dataset.prefix, '')] = event.target.value;
        this.next.disabled = false;
      }, options);
      this.form.addEventListener('submit', event => { event.preventDefault(); this.advance(); }, options);
      this.next.addEventListener('click', () => this.advance(), options);
      this.back.addEventListener('click', () => {
        if (this.showingResults) { this.showingResults = false; this.step = this.steps.length - 1; }
        else this.step = Math.max(0, this.step - 1);
        this.renderStep();
      }, options);
      this.querySelectorAll('[data-gift-reset]').forEach(button => button.addEventListener('click', () => {
        this.answers = {};
        this.form.reset();
        this.step = 0;
        this.showingResults = false;
        this.renderStep();
      }, options));
      this.querySelectorAll('[data-gift-relax]').forEach(button => button.addEventListener('click', () => {
        const field = button.dataset.giftRelax;
        this.answers[field] = 'any';
        const input = this.form.querySelector('input[name="' + this.dataset.prefix + field + '"][value="any"]');
        if (input) input.checked = true;
        this.renderResults();
      }, options));
    }

    text(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll('{' + name + '}', String(value)), this.copy[key] || key);
    }

    open(trigger) {
      this.lastTrigger = trigger;
      this.dialog.showModal();
      if (this.showingResults) this.renderResults();
      else this.renderStep();
    }

    renderStep() {
      this.resultToken++;
      this.results.hidden = true;
      this.form.hidden = false;
      this.steps.forEach((step, index) => { step.hidden = index !== this.step; });
      this.progress.value = this.step + 1;
      this.progressText.textContent = this.text('progress', { current: this.step + 1, total: this.steps.length });
      this.progress.setAttribute('aria-label', this.progressText.textContent);
      this.back.hidden = this.step === 0;
      this.next.hidden = false;
      this.next.textContent = this.step === this.steps.length - 1 ? this.text('show_results') : this.text('next');
      this.next.disabled = !this.answers[this.steps[this.step].dataset.giftStep];
      this.querySelector('[data-gift-reset-footer]').hidden = this.step === 0;
      this.status.textContent = '';
      this.body.scrollTop = 0;
      this.steps[this.step].querySelector('legend').focus({ preventScroll: true });
    }

    advance() {
      if (!this.answers[this.steps[this.step].dataset.giftStep]) return;
      if (this.step < this.steps.length - 1) { this.step += 1; this.renderStep(); }
      else this.renderResults();
    }

    async loadCatalog(audience) {
      const cached = this.catalogCache.get(audience);
      if (cached && Date.now() - cached.time < 120000) return cached.products;
      const path = audience === 'women' ? this.dataset.womenUrl : this.dataset.menUrl;
      if (!path) throw new Error('Catalog not configured');
      let url = new URL(path, location.origin);
      url.searchParams.set('section_id', 'iceyard-ref-gift-guide');
      url.searchParams.set('sort_by', 'best-selling');
      const visited = new Set();
      const products = [];
      while (url) {
        if (url.origin !== location.origin || visited.has(url.href) || visited.size >= 20) throw new Error('Invalid catalog pagination');
        visited.add(url.href);
        const controller = new AbortController();
        const abort = () => controller.abort();
        this.controller.signal.addEventListener('abort', abort, { once: true });
        const timeout = setTimeout(abort, 15000);
        let html;
        try {
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Catalog unavailable');
          html = await response.text();
        } finally {
          clearTimeout(timeout);
          this.controller?.signal.removeEventListener('abort', abort);
        }
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const catalog = parsed.querySelector('[data-gift-catalog]');
        if (!catalog || catalog.dataset.sort !== 'best-selling') throw new Error('Catalog unavailable');
        catalog.querySelectorAll('[data-gift-product]').forEach(node => {
          products.push({ ...node.dataset, id: node.dataset.productId, price: Number(node.dataset.price), rank: Number(node.dataset.rank), audience, available: true });
        });
        const next = catalog.querySelector('[data-gift-catalog-next]')?.getAttribute('href');
        url = next ? new URL(next, url) : null;
        if (url) {
          url.searchParams.set('section_id', 'iceyard-ref-gift-guide');
          url.searchParams.set('sort_by', 'best-selling');
        }
      }
      this.catalogCache.set(audience, { time: Date.now(), products });
      return products;
    }

    async renderResults() {
      this.showingResults = true;
      this.form.hidden = true;
      this.results.hidden = false;
      this.next.hidden = true;
      this.back.hidden = false;
      this.querySelector('[data-gift-reset-footer]').hidden = false;
      this.progress.value = this.steps.length;
      this.progressText.textContent = this.text('results_progress');
      this.progress.setAttribute('aria-label', this.progressText.textContent);
      const token = ++this.resultToken;
      const answers = { ...this.answers };
      this.querySelector('[data-gift-product-grid]').replaceChildren();
      this.querySelector('[data-gift-results-heading]').textContent = 'Henter gaveforslag …';
      this.querySelector('[data-gift-results-intro]').textContent = '';
      this.querySelector('[data-gift-summary]').textContent = '';
      this.querySelector('[data-gift-relax-tools]').hidden = true;
      this.querySelector('[data-gift-ring-note]').hidden = true;
      this.status.textContent = 'Henter bestselgere som passer valgene dine.';
      try {
        this.products = await this.loadCatalog(answers.audience);
      } catch {
        if (token !== this.resultToken || !this.isConnected) return;
        this.querySelector('[data-gift-results-heading]').textContent = 'Kunne ikke hente gaveforslag.';
        this.querySelector('[data-gift-results-intro]').textContent = 'Gå tilbake og prøv igjen, eller se hele sortimentet nedenfor.';
        this.status.textContent = 'Kunne ikke hente gaveforslag. Prøv igjen.';
        return;
      }
      if (token !== this.resultToken || !this.isConnected || !this.dialog.open) return;
      const ranked = rankGiftProducts(this.products, answers);
      const count = ranked.products.length;
      const heading = this.querySelector('[data-gift-results-heading]');
      heading.textContent = !count ? this.text('empty_heading') : !ranked.exactCount ? this.text('near_heading') : this.text('results_heading');
      this.querySelector('[data-gift-results-intro]').textContent = !count ? this.text('empty_text') : ranked.exactCount < count ? this.text('near_text') : this.text('results_text');
      this.querySelector('[data-gift-summary]').textContent = fields.map(field => {
        const value = this.answers[field];
        return isAny(value) ? '' : this.text(field + '_' + value);
      }).filter(Boolean).concat([this.answers.budget === 'any' ? this.text('no_budget') : this.selectedBudgetLabel()]).join(' · ');
      const grid = this.querySelector('[data-gift-product-grid]');
      grid.replaceChildren();
      ranked.products.forEach(product => {
        const card = document.createElement('article');
        card.className = 'iy-gift__product';
        const photo = document.createElement('a');
        photo.className = 'iy-gift__product-photo';
        photo.href = product.url;
        photo.tabIndex = -1;
        photo.setAttribute('aria-hidden', 'true');
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          image.width = 600;
          image.height = 600;
          image.loading = 'lazy';
          photo.appendChild(image);
        } else photo.textContent = this.text('no_image');
        card.appendChild(photo);
        const badge = document.createElement('p');
        badge.className = 'iy-gift__match';
        badge.textContent = this.text(product.exact ? 'exact_badge' : 'near_badge');
        card.appendChild(badge);
        const title = document.createElement('h4');
        const link = document.createElement('a');
        link.href = product.url;
        link.textContent = product.title;
        title.appendChild(link);
        card.appendChild(title);
        const detail = document.createElement('p');
        detail.className = 'iy-gift__product-detail';
        detail.textContent = this.text('finish_' + product.finish) + ' · ' + this.text('category_' + product.category);
        card.appendChild(detail);
        const price = document.createElement('p');
        price.className = 'iy-gift__price';
        price.textContent = product.priceFormatted;
        card.appendChild(price);
        const action = document.createElement('a');
        action.className = 'iy-gift__product-action';
        action.href = product.url;
        action.textContent = this.text(product.category === 'ring' ? 'choose_size' : 'view_product');
        card.appendChild(action);
        grid.appendChild(card);
      });
      this.querySelector('[data-gift-ring-note]').hidden = this.answers.category !== 'ring' && !ranked.products.some(product => product.category === 'ring');
      this.querySelector('[data-gift-relax="budget"]').hidden = this.answers.budget === 'any';
      this.querySelector('[data-gift-relax="finish"]').hidden = isAny(this.answers.finish);
      this.querySelector('[data-gift-relax-tools]').hidden = this.answers.budget === 'any' && isAny(this.answers.finish);
      this.status.textContent = this.text('result_count', { count });
      this.body.scrollTop = 0;
      heading.focus({ preventScroll: true });
    }

    selectedBudgetLabel() {
      const input = [...this.form.querySelectorAll('input')].find(input => input.name === this.dataset.prefix + 'budget' && input.value === this.answers.budget);
      return input?.closest('label').querySelector('[data-gift-option-label]')?.textContent || '';
    }

    disconnectedCallback() {
      this.resultToken++;
      this.controller?.abort();
      this.dialog?.close();
      this.controller = null;
    }
  });
})();

